import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CheckCircle } from 'lucide-react';
import { sidebarMenu } from '@/shared/constant/menu.js';
import NavigationMenu from '@/shared/components/ui/NavigationMenu.jsx';
import Logo from '@/shared/components/modules/Logo.jsx';
import withSidebarToggle from '@/shared/components/HOC/withSidebarToggle.jsx';

import lightLogo from '@/assets/images/Logo.png';
import smallLogo from '@/assets/images/SmallLogo.png';
import whiteLogo from '@/assets/images/WhiteLogo.png';
import smallWhiteLogo from '@/assets/images/SmallWhiteLogo.png';

/* eslint-disable no-unused-vars */
export function SidebarComponent({
  isCollapsed,
  ToggleButton,
  hasSecondarySidebar,
  onHoverChange,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeItem, setActiveItem] = useState('');
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    const basePath = location.pathname;
    if (basePath.startsWith('/rules')) {
      setActiveItem('/rules');
    } else if (
      ['/subscription', '/credit', '/invoices', '/payments', '/billing'].some(
        (path) => basePath.startsWith(path),
      )
    ) {
      setActiveItem('/subscription');
    } else {
      setActiveItem(basePath);
    }
  }, [location.pathname]);

  const handleNavigation = (item) => {
    setActiveItem(item.path);
    navigate(item.path);
  };

  const handleMouseEnter = () => {
    if (hasSecondarySidebar) {
      setIsHovered(true);
      onHoverChange?.(true);
    }
  };

  const handleMouseLeave = () => {
    if (hasSecondarySidebar) {
      setIsHovered(false);
      onHoverChange?.(false);
    }
  };

  const shouldShowExpanded = hasSecondarySidebar ? isHovered : !isCollapsed;

  const getWidth = () => {
    if (hasSecondarySidebar) {
      return shouldShowExpanded ? 'w-60' : 'w-14';
    }
    return shouldShowExpanded ? 'w-72' : 'w-16';
  };

  return (
    <div
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={`border-tertiary-10 fixed top-0 right-0 z-50 flex h-full min-h-0 flex-col border-l bg-white/95 shadow-xl backdrop-blur-xl transition-all duration-300 ease-in-out dark:border-gray-700/50 dark:bg-gray-800/95 dark:shadow-2xl print:!hidden ${getWidth()}`}
    >
      {/* Header Sidebar */}
      <div className="border-tertiary-10 border-b dark:border-gray-700/50">
        {shouldShowExpanded && (
          <div className="relative flex h-16 items-center justify-center px-4">
            <Logo
              src={lightLogo}
              darkSrc={whiteLogo}
              size={hasSecondarySidebar ? 'md' : 'lg'}
              onClick={() => navigate('/')}
              className={hasSecondarySidebar ? 'w-28' : 'w-36'}
            />
            {!hasSecondarySidebar && <ToggleButton position="logo-opposite" />}
          </div>
        )}

        {!shouldShowExpanded && (
          <div className="flex flex-col items-center justify-center">
            <div className="flex h-16 items-center justify-center">
              <Logo
                src={smallLogo}
                darkSrc={smallWhiteLogo}
                size="sm"
                onClick={() => navigate('/')}
                className="h-16 w-auto border-none bg-transparent"
              />
            </div>
            {!hasSecondarySidebar && (
              <ToggleButton position="collapsed-center" />
            )}
          </div>
        )}
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto pb-24">
        <div className={shouldShowExpanded ? 'mt-6' : 'mt-2'}>
          <NavigationMenu
            items={sidebarMenu}
            activeItem={activeItem}
            onItemClick={handleNavigation}
            variant="default"
            showBadges={true}
            showActiveIndicator={true}
            isCollapsed={!shouldShowExpanded}
          />
        </div>

        <div className="flex-1"></div>
      </div>

      {shouldShowExpanded && (
        <div className={hasSecondarySidebar ? 'mb-4 px-3' : 'mb-4 px-4'}>
          <a
            href="https://uptime.barsam.co/status/intelio"
            target="_blank"
            rel="noopener noreferrer"
            className="group block cursor-pointer rounded-lg border border-green-200/60 bg-green-50/50 p-3 transition-all duration-200 hover:border-green-300 hover:bg-green-50 hover:shadow-md dark:border-green-700/50 dark:bg-green-900/20 dark:hover:border-green-600 dark:hover:bg-green-900/40"
          >
            <div className="flex items-center space-x-2 space-x-reverse">
              <div className="h-2 w-2 animate-pulse rounded-full bg-green-500"></div>
              <div className="flex-1">
                <p className="text-xs font-semibold text-green-800 transition-colors group-hover:text-green-900 dark:text-green-400 dark:group-hover:text-green-300">
                  سیستم آنلاین
                </p>
                <p className="text-[10px] text-green-700/70 transition-colors group-hover:text-green-800/70 dark:text-green-500/70 dark:group-hover:text-green-400/70">
                  وضعیت سرویس‌ها
                </p>
              </div>
              <CheckCircle
                size={16}
                className="text-green-600 transition-colors group-hover:text-green-700 dark:text-green-400 dark:group-hover:text-green-300"
              />
            </div>
          </a>
        </div>
      )}
    </div>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export default withSidebarToggle(SidebarComponent);
